import { api } from "../../lib/api/api";
import { path } from "../../lib/path/path";
import { args } from "../../lib/args/args";
import { collectAnswers } from "../answers/collect-answers";
import { expectShape } from "../../lib/expect/expect-shape";
import { is } from "../../lib/is/is";
import { out } from "../../lib/output/out";
import { ACTIONS } from "@/commands/questions/questions-constants";
import type { Batch } from "@/commands/batch/batch";
import { listQuestions } from "@/commands/questions/list-questions";
import { printBatch } from "@/commands/batch/print-batch";
import type { ApiOutputs } from "@/lib/api/api-outputs";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";

/**
 * A session with an unanswered round parks forever. Answering is a normal
 * scope, so an agent can do it; only guardrail decisions need a human.
 */
export async function questionsCommand(parsed: ParsedArgs): Promise<void> {
  const action = args.action(parsed, "questions", ACTIONS, "list");
  const asJson = parsed.booleans.has("json");

  if (action === "list") {
    args.expect(parsed, { flags: ["session"], positional: 1 });
    return await listQuestions(parsed.flags.session, asJson);
  }

  args.expect(parsed, {
    positional: 2,
    ...(action === "answer" ? { flags: ["option", "text", "answers"] } : {}),
  });
  const batchId = args.required(parsed, 1, "batchId");

  if (action === "show") {
    const batch = expectShape<Batch>(
      await api(path`/questions/${batchId}`),
      "GET /questions/{id}",
      { id: is.string, questions: is.array, sessionTitle: is.string },
    );
    if (asJson) {
      out.json(batch);
      return;
    }
    printBatch(batch);
    return;
  }

  if (action === "answer") {
    const result = expectShape<ApiOutputs["questions"]["submit"]>(
      await api(path`/questions/${batchId}/submit`, {
        method: "POST",
        body: { answers: collectAnswers(parsed) },
      }),
      "POST /questions/{id}/submit",
      { answeredCount: is.number },
    );
    if (asJson) {
      out.json(result);
      return;
    }
    out.line(
      `${out.paint("✓", "green")} Answered ${result.answeredCount} question(s); the session resumes.`,
    );
    return;
  }

  const result = await api<ApiOutputs["questions"]["cancel"]>(
    path`/questions/${batchId}/cancel`,
    { method: "POST", body: {} },
  );
  if (asJson) {
    out.json(result);
    return;
  }
  out.line(
    'Round cancelled. The session stays parked; send a message to steer it: `hepha sessions send <id> "…"`',
  );
}
